import { Button, SectionHeader } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useState } from 'react';
import type { IServiceStartingPoint } from '@common/interfaces';
import type { PatientUnavailableServices } from '@common/interfaces/patientUnavailableServices';
import type { orthoServices, ServiceForSelectedServiceType } from '@common/interfaces/processStartWizard';
import { FoundPatientStatus } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { patientSearchStartBarUpdateSchema } from '@common/validation/patient';
import { ProcessStartWizardRow } from '@frontend/components/elements/ProcessStartWizard/ProcessStartWizardRow';
import { ProcessStartWizardText } from '@frontend/components/elements/ProcessStartWizard/ProcessStartWizardText';
import { PatientEnterExtraInformationBar } from '@frontend/components/pages/patients/searchPatient/PatientEnterExtraInformationBar';
import { PatientSearch } from '@frontend/components/pages/patients/searchPatient/PatientSearch';
import { AbortableFetch } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { getStartProcessUrl } from '@frontend/utils/getStartProcessUrl';
import { validate } from '@frontend/utils/validation';
import type { NewPatient } from '../../patients/searchPatient/patientSearchContext';
import { PatientSearchContext } from '../../patients/searchPatient/patientSearchContext';
import { PatientSearchStartBar } from '../../patients/searchPatient/PatientSearchStartBar';
import { ProcessStartWizardCard } from './ProcessStartWizardCard';

interface IPatientSearchRowProps {
    newProcessCustomIdentifier?: string;
    selectedService?: ServiceForSelectedServiceType | orthoServices;
    selectedStartingPoint?: IServiceStartingPoint;
}

export function PatientSearchRow(props: IPatientSearchRowProps) {
    const navigate = useNavigate();
    const { newProcessCustomIdentifier, selectedService, selectedStartingPoint } = props;
    const abortableFetch = new AbortableFetch();
    const selectedEpisodeCategory = selectedService?.episodeCategory.name;
    const [isDisabled, setIsDisabled] = useState(false);

    return (
        <PatientSearch>
            <PatientSearchContext.Consumer>
                {({ newPatient, foundPatientStatus, unavailableServices, duplicatePatientId }) => {
                    return (
                        <>
                            <ProcessStartWizardCard>
                                <PatientSearchStartBar
                                    canCreateAndSeeAllPatients={true}
                                    onNoBsnUrl={getOnNoBsnUrl()}
                                    onStartUrl={(patientId) => getOnStartUrl(patientId)}
                                    selectedService={selectedService}
                                    text={translateKey('home.patientBarLabel')}
                                />
                                {foundPatientStatus === FoundPatientStatus.extraInformationNeeded && (
                                    <PatientEnterExtraInformationBar />
                                )}
                            </ProcessStartWizardCard>
                            {newPatient?.patient.nameFamily && !duplicatePatientId && (
                                <>
                                    <SectionHeader title={translateKey('patient.newPatient')} />
                                    <ProcessStartWizardCard>
                                        <ProcessStartWizardRow
                                            actionComponent={
                                                isServiceAvailable(unavailableServices, newPatient.patient.id) ? (
                                                    <Button
                                                        className="startProcessButton"
                                                        color="secondary"
                                                        isDisabled={isDisabled}
                                                        onClick={() => onStartClick(newPatient)}
                                                        variant="solid"
                                                    >
                                                        {translateKey('common.start')}
                                                    </Button>
                                                ) : (
                                                    <Button hidden={true} />
                                                )
                                            }
                                            icon="profile"
                                            label={getDisplayName(newPatient.patient)}
                                            result={true}
                                            wrapChildren={true}
                                        >
                                            <ProcessStartWizardText
                                                value={translateKey(`genderEnum.${newPatient.patient.gender}`)}
                                            />
                                            {newPatient.patient.identifierInsurance && (
                                                <ProcessStartWizardText
                                                    value={`${translateKey('patient.insurancePolicyNumberShort')}: ${
                                                        newPatient.patient.identifierInsurance
                                                    }`}
                                                />
                                            )}
                                            <ProcessStartWizardText value={newPatient.patient.birthDate || ''} />
                                            <ProcessStartWizardText
                                                value={newPatient.patient.identifierSocialSecurity || ''}
                                            />
                                        </ProcessStartWizardRow>
                                    </ProcessStartWizardCard>
                                </>
                            )}
                        </>
                    );
                }}
            </PatientSearchContext.Consumer>
        </PatientSearch>
    );

    function getOnNoBsnUrl() {
        if (selectedService) {
            return getStartProcessUrl({
                episodeCategory: selectedEpisodeCategory!,
                newProcessCustomIdentifier,
                service: selectedService,
                startingPoint: selectedStartingPoint,
            });
        }

        return '';
    }

    function getOnStartUrl(patientId: string) {
        if (selectedService) {
            return getStartProcessUrl({
                episodeCategory: selectedEpisodeCategory!,
                newProcessCustomIdentifier,
                patientId,
                service: selectedService,
                startingPoint: selectedStartingPoint,
            });
        }

        return '';
    }

    function isServiceAvailable(unavailableServices?: PatientUnavailableServices, patientId?: string) {
        if (!patientId || !selectedService || !unavailableServices) {
            return true;
        }

        return unavailableServices[patientId]?.every((item) => item.serviceId !== selectedService.id);
    }

    async function onStartClick(newPatient: NewPatient) {
        setIsDisabled(true);
        if (newPatient) {
            if (newPatient.patient.id) {
                navigate(`/patients/${newPatient.patient.id}`);
            } else {
                const { errors: validationErrors, item: validatedPatient } = validate(
                    patientSearchStartBarUpdateSchema,
                    newPatient.patient
                );

                if (validationErrors.size === 0) {
                    const newPatientData = {
                        patient: { ...validatedPatient },
                    };

                    const result = await abortableFetch.fetchJson('/api/patients', 'POST', newPatientData);
                    navigate(`/patients/${result.json.id}`);
                } else {
                    const validationError = validationErrors.entries().next().value;
                    throw new Error(
                        `SBVZ/Vecozo patient data validation failed. ${validationError[0]}: ${validationError[1]}`
                    );
                }
            }
        }
        setIsDisabled(false);

        return;
    }
}
